/**
 * Sidebar Component
 * Animated collapsible sidebar for Vite/React
 * Converted from Next.js version
 */

import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Menu, X, Sun, Moon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SidebarProvider, useSidebar } from './sidebar-context';
import { useTheme } from '@/components/theme-provider';

// Re-export for convenience and backward compatibility
export { SidebarProvider, useSidebar };

// ============================================================================
// Types
// ============================================================================

export interface SidebarLink {
  label: string;
  icon: React.ReactNode;
  onClick?: () => void;
  active?: boolean;
  badge?: string; // Optional badge text (e.g., credit count)
  featureKey?: string; // Optional: for feature-flag filtering
  highlight?: boolean; // Optional: gentle pulsing highlight to draw attention (e.g. first project)
  children?: SidebarLink[]; // Optional: nested sub-items rendered indented below the parent
}

// ============================================================================
// Main Sidebar Component
// ============================================================================

export const Sidebar = ({
  children,
  open,
  setOpen,
  animate,
}: {
  children: React.ReactNode;
  open?: boolean;
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  animate?: boolean;
}) => {
  return (
    <SidebarProvider open={open} setOpen={setOpen} animate={animate}>
      {children}
    </SidebarProvider>
  );
};

// ============================================================================
// Sidebar Body
// ============================================================================

export const SidebarBody = (props: React.ComponentProps<typeof motion.div>) => {
  return (
    <>
      <DesktopSidebar {...props} />
      <MobileSidebar {...(props as React.ComponentProps<'div'>)} />
    </>
  );
};

// ============================================================================
// Desktop Sidebar
// ============================================================================

export const DesktopSidebar = ({
  className,
  children,
  ...props
}: React.ComponentProps<typeof motion.div>) => {
  const { open, setOpen, animate } = useSidebar();
  
  return (
    <motion.div
      className={cn(
        'h-full py-4 hidden md:flex md:flex-col bg-sidebar border-r border-sidebar-border w-[280px] flex-shrink-0',
        className
      )}
      animate={{
        width: animate ? (open ? '280px' : '70px') : '280px',
      }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      {...props}
    >
      {children}
    </motion.div>
  );
};

// ============================================================================
// Mobile Sidebar
// ============================================================================

export const MobileSidebar = ({
  className,
  children,
  ...props
}: React.ComponentProps<'div'>) => {
  const { open, setOpen } = useSidebar();
  
  return (
    <>
      <div
        className={cn(
          'h-14 px-4 py-4 flex flex-row md:hidden items-center justify-between bg-sidebar border-b border-sidebar-border w-full'
        )}
        {...props}
      >
        <div className="flex items-center">
          <img
            src="/tenant/estage-logo.svg"
            alt="estage.ai"
            className="h-8 object-contain"
          />
        </div>
        <div className="flex justify-end z-20">
          <Menu
            className="text-sidebar-foreground cursor-pointer h-6 w-6"
            onClick={() => setOpen(!open)}
          />
        </div>
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ x: '-100%', opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: '-100%', opacity: 0 }}
              transition={{
                duration: 0.3,
                ease: 'easeInOut',
              }}
              className={cn(
                'fixed h-full w-full inset-0 bg-sidebar p-6 z-[100] flex flex-col justify-between',
                className
              )}
            >
              <div
                className="absolute right-6 top-6 z-50 text-sidebar-foreground cursor-pointer"
                onClick={() => setOpen(!open)}
              >
                <X className="h-6 w-6" />
              </div>
              {children}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

// ============================================================================
// Sidebar Link Item (for Vite/React - uses onClick instead of href)
// ============================================================================

export const SidebarLinkItem = ({
  link,
  className,
  indented,
}: {
  link: SidebarLink;
  className?: string;
  // Sub-item: indent only while the sidebar is open, so the icon stays aligned
  // with the top-level icons on the collapsed rail (otherwise it gets cropped).
  indented?: boolean;
}) => {
  const { open, animate } = useSidebar();

  return (
    <Button
      variant="ghost"
      onClick={link.onClick}
      className={cn(
        "justify-start w-full gap-4 px-0 pl-6 rounded-none",
        "text-white/80 hover:bg-white/10 hover:text-white",
        // Indent animates in sync with the sidebar's 0.3s width transition.
        indented && "transition-[padding] duration-300 ease-in-out",
        indented && open && "pl-14",
        // Caller className (e.g. dimmed sub-item text) comes before active/
        // highlight so the active state stays fully highlighted.
        className,
        link.active && "bg-white/15 text-white hover:bg-white/20",
        link.highlight && !link.active && "attention-pulse text-white"
      )}
    >
      <span className="flex-shrink-0 relative">
        {link.icon}
        {/* Badge (collapsed view) */}
        {link.badge && !open && (
          <span className="absolute -top-1 -right-1 flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
          </span>
        )}
      </span>
      <motion.span
        animate={{
          display: animate ? (open ? 'inline-block' : 'none') : 'inline-block',
          opacity: animate ? (open ? 1 : 0) : 1,
        }}
        className="truncate overflow-hidden whitespace-nowrap"
      >
        {link.label}
      </motion.span>
      {/* Badge (expanded view) */}
      {link.badge && (
        <motion.div
          animate={{
            display: animate ? (open ? 'inline-flex' : 'none') : 'inline-flex',
            opacity: animate ? (open ? 1 : 0) : 1,
          }}
          className="ml-auto pr-4"
        >
          <Badge variant="secondary" className="px-1.5 py-0.5 text-[10px] h-auto font-bold bg-primary text-primary-foreground hover:bg-primary/90">
            {link.badge}
          </Badge>
        </motion.div>
      )}
    </Button>
  );
};

// ============================================================================
// Logo Components
// ============================================================================

export const SidebarLogo = ({ onClick, className }: { onClick?: () => void, className?: string }) => {
  const { open, animate } = useSidebar();
  
  return (
    <div
      onClick={onClick}
      className={cn(
        "cursor-pointer flex items-center gap-4 py-2 pl-[19px]",
        className
      )}
    >
      <img src="/logo.png" alt="estage.ai" className="h-8 w-8 flex-shrink-0 rounded-lg" />
      <motion.span
        animate={{
          display: animate ? (open ? 'inline-block' : 'none') : 'inline-block',
          opacity: animate ? (open ? 1 : 0) : 1,
        }}
        className="font-bold text-sidebar-foreground text-lg whitespace-pre overflow-hidden"
      >
        estage<span className="text-primary">.ai</span>
      </motion.span>
    </div>
  );
};

// ============================================================================
// User Avatar Component
// ============================================================================

export const SidebarUserItem = ({
  email,
  avatarUrl,
  onClick,
  className
}: {
  email: string;
  avatarUrl?: string | null;
  onClick?: () => void;
  className?: string;
}) => {
  const { open, animate } = useSidebar();

  // Get initials from email
  const initials = email.slice(0, 2).toUpperCase();

  return (
    <Button
      variant="ghost"
      onClick={onClick}
      className={cn(
        "justify-start w-full gap-4 px-0 pl-6 rounded-none",
        "text-white/80 hover:bg-white/10 hover:text-white",
        className
      )}
    >
      {avatarUrl ? (
        <img src={avatarUrl} alt="" className="h-7 w-7 rounded-full flex-shrink-0 object-cover" />
      ) : (
        <div className="h-7 w-7 bg-white/20 rounded-full flex-shrink-0 flex items-center justify-center">
          <span className="text-xs font-medium text-white">{initials}</span>
        </div>
      )}
      <motion.span
        animate={{
          display: animate ? (open ? 'inline-block' : 'none') : 'inline-block',
          opacity: animate ? (open ? 1 : 0) : 1,
        }}
        className="text-sm truncate max-w-[180px] overflow-hidden whitespace-nowrap"
      >
        {email}
      </motion.span>
    </Button>
  );
};

// ============================================================================
// Theme Toggle Component
// ============================================================================

export const SidebarThemeToggle = ({ className }: { className?: string }) => {
  const { open, animate } = useSidebar();
  const { theme, setTheme } = useTheme();
  
  // Determine if currently dark (either explicit dark or system dark)
  const isDark = theme === "dark" || 
    (theme === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches);
  
  const toggleTheme = () => {
    setTheme(isDark ? "light" : "dark");
  };
  
  return (
    <Button
      variant="ghost"
      onClick={toggleTheme}
      className={cn(
        "justify-start w-full gap-4 px-0 pl-6 rounded-none",
        className
      )}
    >
      <span className="flex-shrink-0">
        {isDark ? (
          <Sun className="h-5 w-5 text-muted-foreground" />
        ) : (
          <Moon className="h-5 w-5 text-muted-foreground" />
        )}
      </span>
      <motion.span
        animate={{
          display: animate ? (open ? 'inline-block' : 'none') : 'inline-block',
          opacity: animate ? (open ? 1 : 0) : 1,
        }}
        className="truncate overflow-hidden whitespace-nowrap text-muted-foreground"
      >
        {isDark ? "Light Mode" : "Dark Mode"}
      </motion.span>
    </Button>
  );
};
